/**
 * Mesajlaşma durumu: konuşmalar, açık konuşmanın sayfalı geçmişi, yazıyor bilgisi.
 *
 * Kaynak sunucu: her değişiklik (bizim ya da karşı tarafın) SystemHub olayı olarak gelir ve
 * buraya işlenir. Gönderim iyimserdir — mesaj hemen "gönderiliyor" olarak görünür, sunucu
 * yanıtı (ya da ondan önce gelen anlık olay) gerçeğiyle değiştirir; aynı mesaj iki kez çizilmez.
 */
import { create } from 'zustand';
import { useAuthStore } from '@/entities/session/model/authStore';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { playOctoSound } from '@/shared/lib/sound/octoSounds';
import { toast } from '@/shared/ui/toastStore';
import i18n from '@/shared/i18n';
import { chatApi } from '../api/chatApi';
import {
  conversationKeyOf,
  parseKey,
  type ChatContact,
  type ChatConversation,
  type ChatMessage,
  type ChatReaction,
} from './types';

interface Thread {
  items: ChatMessage[];
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
}

interface Typer {
  userId: string;
  name: string;
  until: number;
}

interface ChatState {
  open: boolean;
  width: number;
  activeKey: string | null;
  conversations: ChatConversation[];
  conversationsLoaded: boolean;
  contacts: ChatContact[];
  threads: Record<string, Thread>;
  typing: Record<string, Typer[]>;
  setOpen: (open: boolean) => void;
  setWidth: (w: number) => void;
  openConversation: (key: string | null) => void;
  loadConversations: () => Promise<void>;
  loadContacts: () => Promise<void>;
  loadThread: (key: string, older?: boolean) => Promise<void>;
  send: (
    key: string,
    input: {
      content: string;
      attachmentIds?: string[];
      attachments?: ChatMessage['attachments'];
      replyTo?: ChatMessage | null;
      type?: string;
      payload?: string;
    },
  ) => Promise<ChatMessage | null>;
  /** `force`: okunmamış sayacı 0 olsa da sunucuya bildir (konuşma açıkken gelen mesaj sayaca hiç eklenmez). */
  markRead: (key: string, force?: boolean) => void;
  applyMessage: (m: ChatMessage, opts?: { updated?: boolean }) => void;
  applyRead: (e: {
    readerUserId: string;
    peerUserId: string | null;
    groupId: string | null;
    readAt: string;
    messageIds?: string[];
  }) => void;
  applyTyping: (e: {
    fromUserId: string;
    fromName: string;
    peerUserId: string | null;
    groupId: string | null;
  }) => void;
  applyReactions: (messageId: string, reactions: ChatReaction[]) => void;
}

const WIDTH_KEY = 'octo.chat.width';
const me = () => useAuthStore.getState().user?.userId?.toLowerCase() ?? '';
const same = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
const emptyThread: Thread = { items: [], hasMore: true, loading: false, loaded: false };

function readWidth() {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY));
    return n >= 360 && n <= 1400 ? n : 480;
  } catch {
    return 480;
  }
}

const attKey = (m: ChatMessage) => m.attachments.map((a) => a.attachmentId).join(',');

/**
 * Mesajları kimliğe göre birleştirir (sırayı zamanla korur). Gönderilmekte olan geçici kayıt,
 * gerçeği hangi yoldan önce gelirse gelsin (anlık olay, sayfa yüklemesi ya da gönderim yanıtı)
 * onunla eşleşip düşer — aynı mesaj bir an bile iki kez görünmez.
 */
function merge(items: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(items.map((m) => [m.messageId, m]));
  for (const m of incoming) byId.set(m.messageId, m);
  const all = [...byId.values()];
  const claimed = new Set<string>();
  const kept = all.filter((m) => {
    if (m.pending !== 'sending') return true;
    const real = all.find(
      (r) =>
        !r.pending &&
        !claimed.has(r.messageId) &&
        same(r.senderId, m.senderId) &&
        r.content === m.content &&
        attKey(r) === attKey(m) &&
        (r.groupId ?? r.recipientId)?.toLowerCase() === (m.groupId ?? m.recipientId)?.toLowerCase(),
    );
    if (!real) return true;
    claimed.add(real.messageId);
    return false;
  });
  return kept.sort((a, b) => a.sentAt.localeCompare(b.sentAt));
}

const readTimers = new Map<string, ReturnType<typeof setTimeout>>();
const forcedRead = new Set<string>();

export const useChatStore = create<ChatState>((set, get) => ({
  open: false,
  width: readWidth(),
  activeKey: null,
  conversations: [],
  conversationsLoaded: false,
  contacts: [],
  threads: {},
  typing: {},

  setOpen: (open) => {
    set({ open });
    if (open) {
      void get().loadConversations();
      const key = get().activeKey;
      if (key) get().markRead(key);
    }
  },
  setWidth: (w) => {
    const width = Math.round(Math.min(1400, Math.max(360, w)));
    set({ width });
    try {
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      /* noop */
    }
  },

  openConversation: (key) => {
    set({ activeKey: key });
    if (!key) return;
    const t = get().threads[key];
    if (!t?.loaded) void get().loadThread(key);
    get().markRead(key);
  },

  loadConversations: async () => {
    try {
      const list = await chatApi.conversations();
      set({ conversations: list, conversationsLoaded: true });
    } catch {
      set({ conversationsLoaded: true });
    }
  },

  loadContacts: async () => {
    try {
      set({ contacts: await chatApi.contacts() });
    } catch {
      /* yetki yoksa liste boş */
    }
  },

  loadThread: async (key, older = false) => {
    const t = get().threads[key] ?? emptyThread;
    if (t.loading || (older && !t.hasMore)) return;
    set((s) => ({ threads: { ...s.threads, [key]: { ...t, loading: true } } }));
    try {
      const before = older ? t.items.find((m) => !m.pending)?.sentAt : undefined;
      const res = await chatApi.messages(parseKey(key), before);
      set((s) => {
        const cur = s.threads[key] ?? emptyThread;
        return {
          threads: {
            ...s.threads,
            [key]: { items: merge(cur.items, res.items), hasMore: res.hasMore, loading: false, loaded: true },
          },
        };
      });
    } catch {
      set((s) => ({
        threads: {
          ...s.threads,
          [key]: { ...(s.threads[key] ?? emptyThread), loading: false, loaded: true },
        },
      }));
    }
  },

  send: async (key, input) => {
    const target = parseKey(key);
    const user = useAuthStore.getState().user;
    const tempId = `tmp-${crypto.randomUUID()}`;
    const temp: ChatMessage = {
      messageId: tempId,
      senderId: user?.userId ?? '',
      senderName: user?.userName ?? null,
      recipientId: target.peerUserId ?? null,
      groupId: target.groupId ?? null,
      content: input.content,
      type: (input.type as ChatMessage['type']) ?? (input.attachments?.length ? 'file' : 'text'),
      payload: input.payload ?? null,
      sentAt: new Date().toISOString(),
      editedAt: null,
      deleted: false,
      replyTo: input.replyTo
        ? {
            messageId: input.replyTo.messageId,
            senderId: input.replyTo.senderId,
            senderName: input.replyTo.senderName,
            preview: input.replyTo.content,
          }
        : null,
      attachments: input.attachments ?? [],
      reactions: [],
      readAt: null,
      readCount: 0,
      recipientCount: 0,
      pending: 'sending',
    };
    const put = (fn: (items: ChatMessage[]) => ChatMessage[]) =>
      set((s) => {
        const cur = s.threads[key] ?? { ...emptyThread, loaded: true, hasMore: false };
        return { threads: { ...s.threads, [key]: { ...cur, items: fn(cur.items) } } };
      });
    put((items) => [...items, temp]);
    try {
      const saved = await chatApi.send({
        ...target,
        content: input.content,
        type: input.type,
        payload: input.payload,
        replyToId: input.replyTo?.messageId ?? null,
        attachmentIds: input.attachmentIds,
      });
      put((items) =>
        merge(
          items.filter((m) => m.messageId !== tempId),
          [saved],
        ),
      );
      get().applyMessage(saved);
      return saved;
    } catch {
      put((items) => items.map((m) => (m.messageId === tempId ? { ...m, pending: 'failed' } : m)));
      return null;
    }
  },

  markRead: (key, force = false) => {
    if (force) forcedRead.add(key);
    const prev = readTimers.get(key);
    if (prev) clearTimeout(prev);
    readTimers.set(
      key,
      setTimeout(() => {
        readTimers.delete(key);
        const conv = get().conversations.find((c) => c.key === key);
        const forced = forcedRead.delete(key);
        if (!forced && conv && conv.unread === 0) return;
        set((s) => ({
          conversations: s.conversations.map((c) => (c.key === key ? { ...c, unread: 0 } : c)),
        }));
        void chatApi.read(parseKey(key));
      }, 250),
    );
  },

  applyMessage: (m, opts = {}) => {
    const my = me();
    const key = conversationKeyOf(m, my);
    const mine = same(m.senderId, my);
    const s = get();
    const thread = s.threads[key];
    if (thread?.loaded) {
      set((st) => ({ threads: { ...st.threads, [key]: { ...thread, items: merge(thread.items, [m]) } } }));
    }
    // Yazan kişinin "yazıyor" göstergesi mesajla birlikte söner.
    if (!mine && s.typing[key]?.some((t) => same(t.userId, m.senderId)))
      set((st) => ({
        typing: { ...st.typing, [key]: (st.typing[key] ?? []).filter((t) => !same(t.userId, m.senderId)) },
      }));
    if (opts.updated) {
      set((st) => ({
        conversations: st.conversations.map((c) =>
          c.lastMessage?.messageId === m.messageId
            ? { ...c, lastMessage: { ...c.lastMessage, content: m.content, type: m.type } }
            : c,
        ),
      }));
      return;
    }

    const viewing = s.open && s.activeKey === key && document.visibilityState === 'visible';
    const exists = s.conversations.some((c) => c.key === key);
    if (!exists) {
      void get().loadConversations();
    } else {
      set((st) => ({
        conversations: st.conversations
          .map((c) =>
            c.key === key
              ? {
                  ...c,
                  lastMessage: {
                    messageId: m.messageId,
                    sentAt: m.sentAt,
                    content: m.content,
                    type: m.type,
                    senderId: m.senderId,
                    senderName: m.senderName,
                  },
                  unread: mine || viewing ? c.unread : c.unread + 1,
                }
              : c,
          )
          .sort((a, b) => (b.lastMessage?.sentAt ?? '').localeCompare(a.lastMessage?.sentAt ?? '')),
      }));
    }
    if (!mine) {
      if (viewing) get().markRead(key, true);
      const prefs = usePrefsStore.getState().prefs;
      // Konuşmanın içindeyken de çalar ama daha kısık: yeni mesajı fark ettirmek yeterli.
      playOctoSound(prefs.soundMessage, viewing ? prefs.soundVolume * 0.4 : prefs.soundVolume);
      // O konuşmaya bakmıyorsa tıklanabilir bildirim (uygulama kapalı/arka plandaysa push gelir).
      if (!viewing && !(s.open && s.activeKey === key)) {
        const t = i18n.t.bind(i18n);
        const conv = get().conversations.find((c) => c.key === key);
        const sender = m.senderName ?? conv?.title ?? '';
        const title = m.groupId && conv?.title ? `${sender} · ${conv.title}` : sender;
        const body =
          m.type === 'image'
            ? `📷 ${t('chat.photo')}`
            : m.type === 'share'
              ? `🔗 ${m.content || t('chat.sharedScreen')}`
              : m.content || (m.attachments.length ? `📎 ${m.attachments[0]!.fileName}` : '');
        toast.action(title || t('chat.title'), body.length > 140 ? `${body.slice(0, 140)}…` : body, () => {
          get().setOpen(true);
          get().openConversation(key);
        });
      }
    }
  },

  applyRead: (e) => {
    const my = me();
    const key = e.groupId
      ? `g:${e.groupId}`
      : same(e.readerUserId, my)
        ? `u:${e.peerUserId}`
        : `u:${e.readerUserId}`;
    if (same(e.readerUserId, my)) {
      set((s) => ({ conversations: s.conversations.map((c) => (c.key === key ? { ...c, unread: 0 } : c)) }));
      return;
    }
    const thread = get().threads[key];
    if (!thread) return;
    // Sunucu okunan mesajların kimliklerini gönderir: tam olarak onlar işaretlenir (grupta aynı
    // okuyucu bir mesajı iki kez saymaz). Kimlik yoksa (eski sunucu) zamana göre işaretlenir.
    const ids = e.messageIds ? new Set(e.messageIds.map((x) => x.toLowerCase())) : null;
    const readTime = new Date(e.readAt).getTime();
    const hit = (m: ChatMessage) =>
      same(m.senderId, my) &&
      !m.pending &&
      (ids ? ids.has(m.messageId.toLowerCase()) : new Date(m.sentAt).getTime() <= readTime);
    set((s) => ({
      threads: {
        ...s.threads,
        [key]: {
          ...thread,
          items: thread.items.map((m) =>
            !hit(m)
              ? m
              : e.groupId
                ? ids
                  ? { ...m, readCount: Math.min(m.recipientCount || m.readCount + 1, m.readCount + 1) }
                  : m
                : { ...m, readAt: m.readAt ?? e.readAt },
          ),
        },
      },
    }));
  },

  applyTyping: (e) => {
    const key = e.groupId ? `g:${e.groupId}` : `u:${e.fromUserId}`;
    const until = Date.now() + 4000;
    set((s) => {
      const list = (s.typing[key] ?? []).filter((t) => !same(t.userId, e.fromUserId) && t.until > Date.now());
      return { typing: { ...s.typing, [key]: [...list, { userId: e.fromUserId, name: e.fromName, until }] } };
    });
    setTimeout(() => {
      set((s) => ({
        typing: { ...s.typing, [key]: (s.typing[key] ?? []).filter((t) => t.until > Date.now()) },
      }));
    }, 4100);
  },

  applyReactions: (messageId, reactions) => {
    set((s) => {
      const threads = { ...s.threads };
      for (const [k, t] of Object.entries(threads)) {
        if (t.items.some((m) => m.messageId === messageId))
          threads[k] = {
            ...t,
            items: t.items.map((m) => (m.messageId === messageId ? { ...m, reactions } : m)),
          };
      }
      return { threads };
    });
  },
}));

/** Başlıktaki rozet: bütün konuşmaların okunmamış toplamı. */
export const selectUnreadTotal = (s: ChatState) => s.conversations.reduce((n, c) => n + c.unread, 0);
